import React from "react";
import { fetchListPazirandeClubs } from "@/api/merchant/request.get";
import { fetchRegisterSingle } from "@/api/merchant/request.post";
import CustomSelectNormal from "@/shared/components/common/select/selectNormal";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { Grid, TextField } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import AdapterMomentJalali from "@/shared/components/common/datePeacker";
import moment from "jalali-moment";
import { toEnglishNumber, toPersianNumber, } from "@/hooks/useThousandsSeparator";

interface Props {
  onSubmitSuccess?: () => void;
}

// zod schema for validation
const schema = z.object({
  name: z.string().nonempty("نام الزامی است"),
  family: z.string().nonempty("نام خانوادگی الزامی است"),
  NID: z.string().nonempty("کد ملی الزامی است"),
  phoneNumber: z.string().nonempty("شماره تماس الزامی است"),
  birthday: z.string().nonempty("تاریخ تولد الزامی است"),
  job: z.string().optional(),
  email: z.string().email("ایمیل نامعتبر است").optional().or(z.literal("")),
  postal: z.string().optional(),
  address: z.string().optional(),
  club_id: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

/**
 * ContentFirstTab
 *
 * فرم ثبت‌نام انفرادی مشتری با قابلیت:
 * - مدیریت فرم با React Hook Form و Zod
 * - اعتبارسنجی لحظه‌ای فیلدها
 * - ارسال اطلاعات با React Query Mutation
 * - واکشی داینامیک لیست سازمان‌ها
 * - پشتیبانی کامل از ریسپانسیو در موبایل، تبلت و دسکتاپ
 *
 * نسخه‌ی ریسپانسیو‌شده شامل:
 * - استفاده از Grid ریسپانسیو MUI با breakpoints
 * - کاهش فاصله‌ها در موبایل
 * - افزایش عرض فیلدها در نمایشگرهای کوچک
 *
 * @param onSubmitSuccess تابع اختیاری اجراشونده پس از ثبت موفق
 * @returns JSX.Element
 */

const ContentFirstTab: React.FC<Props> = ({ onSubmitSuccess }) => {
  const { data: dataListPazirandeClubs, isFetched, isLoading, } = useQuery({
    queryKey: ["ListPazirandeClubsContent-data"],
    queryFn: fetchListPazirandeClubs,
  });

  const { control, handleSubmit, formState: { errors }, } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      family: "",
      NID: "",
      phoneNumber: "",
      birthday: "",
      job: "",
      email: "",
      postal: "",
      address: "",
      club_id: "",
    },
  });

  const { mutate: submitForm } = useMutation({
    mutationFn: fetchRegisterSingle,
    onSuccess: () => {
      toast.success("با موفقیت ثبت شد");
      onSubmitSuccess?.();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message || "خطا در ثبت اطلاعات");
    },
  });

  const onSubmit = (data: FormValues) => {
    submitForm(data);
  };


  return (
    <StackKit alignItems="normal" sx={{bgcolor: "#2C2C2C",width: "100%",borderRadius: "10px",padding: { xs: "10px", sm: "15px 20px" }}}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
        <StackKit sx={{ width: "100%", my: 3 }} alignItems="start">
          <TypoGraphyKit variant="body1">
            مشخصات کاربر مورد نظر را وارد نمایید. (تکمیل بخش‌های ستاره‌دار الزامی است)
          </TypoGraphyKit>
        </StackKit>

        <Grid container spacing={4}>

          {/* ستون چپ */}
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex", justifyContent:'space-between',flexDirection: "column", gap: 3 }}>
            {[
              { name: "name", label: "نام" },
              { name: "family", label: "نام خانوادگی" }
            ].map(({ name, label }) => (
              <Controller
                key={name}
                name={name as any}
                control={control}
                render={({ field }) => (
                  <StackKit direction="column" sx={{ flexWrap: "wrap","& > *": { marginLeft: "0 !important" }, }} justifyContent="space-between">
                    <TypoGraphyKit className="text-right" variant="body1" sx={{ mb: { xs: 1, sm: 0 } }}>
                      {label}
                    </TypoGraphyKit>
                    <TextField {...field} sx={{ width: '100%' }} error={!!errors.name} helperText={errors.name?.message}/>
                  </StackKit>
                )}
              />
            ))}

            {/* کد ملی */}
            <Controller
              name="NID"
              control={control}
              render={({ field }) => (
                <StackKit direction="column" sx={{ flexWrap: "wrap","& > *": { marginLeft: "0 !important" }, }} justifyContent="space-between">
                  <TypoGraphyKit className="text-right" variant="body1">
                    کد ملی
                  </TypoGraphyKit>
                  <TextField {...field} sx={{ width:'100%'}} error={!!errors.NID} helperText={errors.NID?.message} value={field.value ? toPersianNumber(field.value) : ""}
                    onChange={(e) => {
                      const english = toEnglishNumber(e.target.value).replace(/[^0-9]/g, "");
                      if (english.length <= 10) field.onChange(english);
                    }}
                  />
                </StackKit>
              )}
            />

            {/* شماره تماس */}
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <StackKit direction="column" sx={{ flexWrap: "wrap","& > *": { marginLeft: "0 !important" }, }} justifyContent="space-between">
                  <TypoGraphyKit className="text-right" variant="body1">
                    شماره تماس 
                  </TypoGraphyKit>
                  <TextField {...field} sx={{ width:'100%'}} error={!!errors.phoneNumber} helperText={errors.phoneNumber?.message} value={field.value ? toPersianNumber(field.value) : ""}
                    onChange={(e) => {
                      const english = toEnglishNumber(e.target.value).replace(/[^0-9]/g, "");
                      if (english.length <= 11) field.onChange(english);
                    }}
                  />
                </StackKit>
              )}
            />
          </Grid>

          {/* ستون راست */}
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

            {/* تاریخ تولد */}
            <Controller
              name="birthday"
              control={control}
              render={({ field, fieldState }) => (
                <StackKit direction="column" sx={{ width: "100%" }}>
                  <StackKit direction="column" justifyContent="space-between" sx={{ flexWrap: "wrap","& > *": { marginLeft: "0 !important" }, }}>
                    <TypoGraphyKit className="text-right" variant="body1">
                      تاریخ تولد
                    </TypoGraphyKit>
                    <StackKit sx={{ width: '100%'}}>
                      <AdapterMomentJalali
                        sx={{ width: "100%", height: "50px" }}
                        widthProps="100%"
                        heightProps="50px"
                        valueDate={field.value ? moment(field.value, "jYYYY/jMM/jDD") : null}
                        errorHandle={fieldState.error}
                        handleChange={(newValue: any) => {
                          field.onChange(newValue?.format("jYYYY/jMM/jDD") || "");
                        }}
                      />
                      {fieldState.error && (
                        <TypoGraphyKit variant="caption" sx={{ color: "#d32f2f" }}>
                          {fieldState.error.message}
                        </TypoGraphyKit>
                      )}
                    </StackKit>
                  </StackKit>
                </StackKit>
              )}
            />

            {[
              { name: "job", label: "شغل" },
              { name: "email", label: "ایمیل" },
              { name: "postal", label: "کد پستی" }
            ].map(({ name, label }) => (
              <Controller
                key={name}
                name={name as any}
                control={control}
                render={({ field }) => (
                  <StackKit direction="column" sx={{ flexWrap: "wrap","& > *": { marginLeft: "0 !important" } }}>
                    <TypoGraphyKit className="text-right" variant="body1">
                      {label}
                    </TypoGraphyKit>
                    <TextField {...field} sx={{ width: '100%'}} error={!!errors.name} helperText={errors.name?.message}/>
                  </StackKit>
                )}
              />
            ))}
          </Grid>

          {/* آدرس */}
          <Grid size={12}>
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <StackKit direction="row" sx={{flexWrap: "wrap","& > *": { marginLeft: "0 !important" },}}>
                  <TypoGraphyKit variant="body1">
                    آدرس محل سکونت
                  </TypoGraphyKit>
                  <TextField {...field} sx={{width: "100%",}}/>
                </StackKit>
              )}
            />
          </Grid>


          {/* سازمان مشتری */}
          <Grid size={12}>
            <Controller
              name="club_id"
              control={control}
              render={({ field }) => (
                <StackKit direction="row" sx={{ flexWrap: "wrap", gap: 2 }}>
                  <TypoGraphyKit variant="body1">
                    سازمان مشتری
                  </TypoGraphyKit>
                  <CustomSelectNormal label="سازمان مشتری" value={field.value || ""} onChange={(e) => field.onChange(`${e.target.value}` || "")} options={dataListPazirandeClubs ?? []} getOptionLabel={(item) => `${item.title}`} getOptionValue={(item) => item.id} width={"100%"}/>
                </StackKit>
              )}
            />
          </Grid>

          {/* دکمه ثبت */}
          <Grid size={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <CustomButton type="submit" sx={{ bgcolor: "#5A5A5A", px: 3, py: 1.5 }}>
              <TypoGraphyKit>ثبت نام کاربر</TypoGraphyKit>
            </CustomButton>
          </Grid>

        </Grid>
      </form>
    </StackKit>
  );

};


export default ContentFirstTab;
